import type {
  FieldItemStatus,
  FieldMissionHeader,
  FieldProblemReason,
  MissionListRow,
} from '@groupe-reca/contracts'

export type {
  FieldItemEvent,
  FieldItemStatus,
  FieldMissionHeader,
  FieldMissionItem,
  FieldProblemReason,
  FieldProgress,
  MissionDetail,
  MissionListResult,
  MissionListRow,
  MissionsDashboard,
  MissionTrack,
} from '@groupe-reca/contracts'
export { fieldProblemReasonLabels, missionDurations } from '@groupe-reca/contracts'

export type MissionListTab = 'current' | 'history'
/**
 * `TO_REVIEW` : terminée avec une résidence encore à faire ou en problème non classé par le bureau — file du badge
 * de navigation Missions (`notifications_summary_v1`). `COMPLETED_UNRESOLVED` garde la mission même après
 * classement.
 */
export type MissionStatusFilter =
  '' | 'IN_PROGRESS' | 'PAUSED' | 'COMPLETED' | 'COMPLETED_UNRESOLVED' | 'TO_REVIEW' | 'CANCELLED'

export interface MissionListFilters {
  tab: MissionListTab
  search: string
  status: MissionStatusFilter
  dateFrom: string
  dateTo: string
  page: number
  pageSize: number
}

export type MissionDisplayStatus =
  'IN_PROGRESS' | 'PAUSED' | 'COMPLETED' | 'COMPLETED_UNRESOLVED' | 'CANCELLED'

/** Une sortie terminée avec des résidences non résolues n'est pas une opération entièrement réalisée. */
export function missionDisplayStatus(
  mission: Pick<FieldMissionHeader | MissionListRow, 'status' | 'legacyStatus'>,
): MissionDisplayStatus {
  if (mission.status === 'COMPLETED' && mission.legacyStatus === 'terminee_avec_anomalies')
    return 'COMPLETED_UNRESOLVED'
  return mission.status
}

export const missionStatusLabels: Record<MissionDisplayStatus, string> = {
  IN_PROGRESS: 'En cours',
  PAUSED: 'En pause',
  COMPLETED: 'Terminée',
  COMPLETED_UNRESOLVED: 'Terminée avec résidences non résolues',
  CANCELLED: 'Annulée',
}

export const itemStatusLabels: Record<FieldItemStatus, string> = {
  WAITING: 'À faire',
  COMPLETED: 'Terminée',
  PROBLEM: 'Problème',
}

export const itemEventLabels: Record<string, string> = {
  COMPLETED: 'Travail confirmé',
  PROBLEM_REPORTED: 'Problème signalé',
  PROBLEM_RESOLVED: 'Problème résolu, travail confirmé',
  CLEARED: 'Classée par le bureau',
  REOPENED: 'Rouverte (correction sur la tablette)',
}

export const missionEventLabels: Record<string, string> = {
  mission_creee: 'Mission créée sur la tablette',
  mission_debutee: 'Sortie démarrée',
  mission_pausee: 'Pause',
  mission_reprise: 'Reprise',
  mission_terminee: 'Sortie terminée',
  mission_terminee_avec_anomalies: 'Sortie terminée avec résidences non résolues',
  mission_annulee: 'Mission annulée',
}

export function problemReasonLabel(
  code: FieldProblemReason | null,
  labels: Record<FieldProblemReason, string>,
): string {
  return code ? labels[code] : '—'
}

/** Ligne d'adresse d'une résidence de mission (instantané figé au départ), unité comprise. */
export function itemAddress(snapshot: {
  addressLine1: string | null
  addressLine2: string | null
  formattedAddress: string | null
}): string {
  const line = [snapshot.addressLine1, snapshot.addressLine2].filter(Boolean).join(', ')
  if (line !== '') return line
  return snapshot.formattedAddress ?? 'Adresse inconnue'
}

// ------------------------------------------------------------------ Formats (heure de Toronto)

const dateTimeFormat = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'America/Toronto',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})
const timeFormat = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'America/Toronto',
  hour: '2-digit',
  minute: '2-digit',
})
const hoursFormat = new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 0, maximumFractionDigits: 2 })

export function formatTorontoDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFormat.format(date)
}

export function formatTorontoTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : timeFormat.format(date)
}

/** Relevé du compteur : heures moteur cumulées, jamais des kilomètres. */
export function formatEngineHours(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${hoursFormat.format(value)} h`
}

/** « 2 h 05 », « 12 min ». */
export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${String(minutes)} min`
  return `${String(Math.floor(minutes / 60))} h ${String(minutes % 60).padStart(2, '0')}`
}

/**
 * « il y a 3 min ». Une position reçue récemment ne prouve pas à elle seule que l'opérateur est connecté :
 * le libellé décrit une réception, rien de plus.
 */
export function formatReceivedAgo(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return 'Aucune position reçue'
  const seconds = Math.max(0, Math.round((now.getTime() - new Date(value).getTime()) / 1000))
  if (seconds < 60) return 'Dernière position reçue il y a moins d’une minute'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `Dernière position reçue il y a ${String(minutes)} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24)
    return `Dernière position reçue il y a ${String(hours)} h ${String(minutes % 60).padStart(2, '0')}`
  return `Dernière position reçue le ${formatTorontoDateTime(value)}`
}

// ------------------------------------------------------------------ Erreurs

const errorMessages: Record<string, string> = {
  MISSION_SUPERVISE_FORBIDDEN: 'Votre rôle ne permet pas cette action.',
  MISSION_READ_FORBIDDEN: 'Votre rôle ne permet pas de consulter les missions.',
  MISSION_NOT_FOUND: 'Cette mission est introuvable.',
  MISSION_NOT_OPEN: 'Cette mission n’est plus ouverte.',
  MISSION_STILL_OPEN: 'La mission est encore ouverte : la résidence peut encore être traitée sur le terrain.',
  MISSION_ITEM_NOT_UNRESOLVED: 'Cette résidence n’est plus en attente de traitement.',
  MISSION_CANCEL_REASON_REQUIRED: 'Indiquez le motif de l’annulation (3 caractères au moins).',
  MISSION_CLEAR_REASON_REQUIRED: 'Indiquez comment la résidence a été traitée (3 caractères au moins).',
  MISSION_HOUR_METER_REASON_REQUIRED: 'Indiquez le motif de la correction (3 caractères au moins).',
  MISSION_ENGINE_HOURS_INVALID: 'Relevé invalide : un nombre positif, au plus deux décimales.',
  MISSION_EQUIPMENT_IN_USE:
    'Ce tracteur est engagé dans une mission ouverte : corrigez le compteur après sa fin.',
  MISSION_EQUIPMENT_NOT_FOUND: 'Ce tracteur est introuvable.',
  MISSION_IDEMPOTENCY_KEY_REUSED: 'Cette demande a déjà servi. Rouvrez la fenêtre et recommencez.',
  MISSION_COMMAND_IN_FLIGHT: 'La même demande est en cours de traitement. Patientez un instant.',
  MISSION_DETECTION_PROFILE_CONFLICT:
    'Une autre personne vient de publier une version du profil. Relisez-la avant de publier la vôtre.',
  MISSION_DETECTION_AUTO_REQUIRES_VALIDATION:
    'Le mode automatique exige la validation de RECA sur le terrain et un motif.',
  MISSION_DETECTION_LABEL_INVALID: 'Indiquez un libellé (80 caractères au plus).',
  MISSION_DETECTION_REASON_INVALID: 'Le motif compte de 3 à 1000 caractères.',
  MISSION_DETECTION_MODE_INVALID: 'Mode de détection inconnu.',
  MISSION_DETECTION_PARAMETERS_INVALID: 'Un réglage est absent ou invalide.',
  MISSION_DETECTION_PARAMETERS_OUT_OF_RANGE: 'Un réglage sort des bornes permises.',
  MISSION_DETECTION_RADII_ORDER: 'Les rayons doivent respecter : capture < travail < sortie.',
}

export class MissionCommandError extends Error {
  constructor(public readonly code: string) {
    super(errorMessages[code] ?? 'L’opération n’a pas pu être terminée. Réessayez dans un instant.')
    this.name = 'MissionCommandError'
  }
}

/** Code métier (`raise exception using message = 'CODE'`) extrait d'une erreur Supabase, sinon `UNEXPECTED`. */
export function missionErrorCode(message: string | undefined): string {
  const code = message?.trim() ?? ''
  return /^[A-Z][A-Z_]+$/.test(code) ? code : 'UNEXPECTED'
}
